/* eslint-disable max-len */
import { ReactElement, useEffect, useState } from 'react';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  matchPath,
} from 'react-router-dom';
import classNames from 'classnames';

import { useAppSelector } from 'app/hooks';
import { useToursPolling } from 'app/hooks/useToursPolling';
import { signOut } from 'app/services/firebase/auth';
import { ROUTES } from 'app/constants';

import dashboardLogo from 'app/assets/images/dashboard-logo.png';

const navItemIdle =
  'flex items-center gap-2.5 px-3 py-2 rounded-2xl text-[13px] font-medium text-black/50 hover:text-black/75 hover:bg-black/[0.04] transition-all';
const navItemActive =
  'flex items-center gap-2.5 px-3 py-2 rounded-2xl text-[13px] font-semibold text-black/[0.85] bg-black/[0.07] transition-all';

const getInitials = (name?: string | null): string => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

interface NavLinkProps {
  to: string;
  active: boolean;
  icon: ReactElement;
  label: string;
}

const SidebarNavLink = ({ to, active, icon, label }: NavLinkProps) => (
  <Link to={to} className={active ? navItemActive : navItemIdle}>
    {icon}
    <span className="max-md:hidden">{label}</span>
  </Link>
);

const CalendarIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0"
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </svg>
);

const ToolsIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0"
  >
    <path d="M14.7 6.3a4 4 0 0 0 4.95 4.95l-9.05 9.05a2 2 0 1 1-2.83-2.83l9.05-9.05a4 4 0 0 0-2.12-2.12z" />
    <path d="M9 4.5L7 6.5l-2-2 2-2 2 2z" />
  </svg>
);

const PlayIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M10 9l5 3-5 3V9z" />
  </svg>
);

interface MobileNavLinkProps extends NavLinkProps {
  onNavigate: () => void;
}

const MobileNavLink = ({
  to,
  active,
  icon,
  label,
  onNavigate,
}: MobileNavLinkProps) => (
  <Link
    to={to}
    onClick={onNavigate}
    className={`flex items-center gap-4 px-5 py-4 text-[17px] ${
      active ? 'font-semibold text-black/[0.9]' : 'font-medium text-black/75'
    }`}
  >
    <span className="flex items-center justify-center w-6 h-6 text-black/70">
      {icon}
    </span>
    <span>{label}</span>
  </Link>
);

function DashboardLayout(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.authState.user);

  // Background-refresh the tours list so newly created tours and collaborator
  // additions appear without a manual page refresh.
  useToursPolling();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    setMobileMenuOpen(false);
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out failed', err);
    }
    navigate(ROUTES.SIGN_IN, { replace: true });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isActive = (path: string): boolean => {
    if (path === ROUTES.PERSONAL_DASHBOARD) {
      return location.pathname.startsWith('/personal/dashboard');
    }
    return !!matchPath({ path, end: true }, location.pathname);
  };

  const displayName = user?.name || user?.email || 'User';

  // Tour view owns its own sidebar (property + section nav), so the global
  // sidebar is suppressed on that route.
  const isTourView = !!matchPath(
    { path: ROUTES.PERSONAL_TOUR_VIEW, end: false },
    location.pathname,
  );

  if (isTourView) {
    return (
      <div className="h-[100dvh] overflow-hidden text-[#151A30]">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden text-[#151A30]">
      <aside className="sticky top-0 h-[100dvh] p-4 shrink-0 max-md:hidden">
        <div className="bg-white rounded-[2rem] shadow-[0_6px_6px_rgba(0,0,0,0.18),0_0_20px_rgba(0,0,0,0.08)] h-full w-[228px] flex flex-col p-5">
          <div className="flex items-center px-1 pb-4 border-b border-black/10 mb-4 max-md:justify-center max-md:px-0">
            <a
              href="https://www.the3dapp.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="The 3D App home"
              className="block w-full"
            >
              <img
                src={dashboardLogo}
                alt="The 3D App"
                className="w-full h-auto max-h-12 object-contain max-md:w-10"
              />
            </a>
          </div>

          <nav className="flex-1 flex flex-col gap-1 overflow-y-auto">
            <SidebarNavLink
              to={ROUTES.PERSONAL_DASHBOARD}
              active={isActive(ROUTES.PERSONAL_DASHBOARD)}
              label="Dashboard"
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="shrink-0"
                >
                  <rect x="2" y="2" width="7" height="7" rx="1.5" />
                  <rect x="11" y="2" width="7" height="7" rx="1.5" />
                  <rect x="2" y="11" width="7" height="7" rx="1.5" />
                  <rect x="11" y="11" width="7" height="7" rx="1.5" />
                </svg>
              }
            />
            <SidebarNavLink
              to={ROUTES.PERSONAL_TOUR_UPLOAD}
              active={isActive(ROUTES.PERSONAL_TOUR_UPLOAD)}
              label="Upload tour"
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                >
                  <path d="M10 13V4" />
                  <path d="M6.5 7.5 10 4l3.5 3.5" />
                  <path d="M3.5 12.5v2.5a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5v-2.5" />
                </svg>
              }
            />
            <button
              type="button"
              onClick={() =>
                window.open(
                  'https://www.the3dapp.com/book',
                  '_blank',
                  'noreferrer',
                )
              }
              className={`${navItemIdle} bg-transparent border-0 focus:outline-none cursor-pointer text-left w-full`}
            >
              <CalendarIcon />
              <span className="max-md:hidden">Book a Tour</span>
            </button>
            <button
              type="button"
              onClick={() =>
                window.open(
                  'https://www.the3dapp.com/applications/diy',
                  '_blank',
                  'noreferrer',
                )
              }
              className={`${navItemIdle} bg-transparent border-0 focus:outline-none cursor-pointer text-left w-full`}
            >
              <ToolsIcon />
              <span className="max-md:hidden">DIY</span>
            </button>
            <button
              type="button"
              className={`${navItemIdle} bg-transparent border-0 focus:outline-none cursor-pointer text-left w-full`}
            >
              <PlayIcon />
              <span className="max-md:hidden">Tutorials</span>
            </button>
          </nav>

          <div className="pt-4 mt-2 border-t border-black/10">
            <Link
              to={ROUTES.PERSONAL_PROFILE_EDIT}
              aria-label="Profile settings"
              className={classNames(
                'flex items-center gap-2.5 px-1 py-1 rounded-md hover:bg-black/[0.04] transition-colors max-md:justify-center max-md:px-0',
                isActive(ROUTES.PERSONAL_PROFILE_EDIT) && 'bg-black/[0.05]',
              )}
            >
              <span className="w-9 h-9 rounded-full bg-black/85 text-white flex items-center justify-center font-semibold text-[12px] shrink-0 shadow-sm">
                {getInitials(displayName)}
              </span>
              <span className="flex-1 flex flex-col min-w-0 text-left max-md:hidden">
                <span className="font-semibold text-[13px] text-black/85 truncate">
                  {displayName}
                </span>
              </span>
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="mt-2 flex items-center gap-2 w-full px-2 py-2 rounded-md text-[13px] font-medium text-black/60 hover:text-black/90 hover:bg-black/[0.04] transition-colors bg-transparent border-0 focus:outline-none cursor-pointer max-md:justify-center"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <path d="M10 12l3-4-3-4M13 8H6M6 3H3v10h3" />
              </svg>
              <span className="max-md:hidden">Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      <header className="md:hidden fixed top-0 left-0 right-0 z-40 h-16 bg-white flex items-center justify-between px-2.5 pt-2 pb-0">
        <a
          href="https://www.the3dapp.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="The 3D App home"
          className="-ml-5 flex items-center"
        >
          <img
            src={dashboardLogo}
            alt="The 3D App"
            className="h-12 w-auto object-contain"
          />
        </a>
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setMobileMenuOpen(true)}
          className="w-9 h-9 flex flex-col items-center justify-center gap-[5px] bg-transparent border-0 focus:outline-none p-1"
        >
          <span className="block w-[22px] h-[2px] bg-black rounded-sm" />
          <span className="block w-[22px] h-[2px] bg-black rounded-sm" />
          <span className="block w-[22px] h-[2px] bg-black rounded-sm" />
        </button>
      </header>

      <main className="flex-1 min-w-0 min-h-0 flex flex-col pt-4 pb-0 pr-4 max-md:pr-2 max-md:pl-2 max-md:pt-16 max-md:pb-2">
        <Outlet />
      </main>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-white flex flex-col">
          <div className="h-16 px-2.5 pt-2 pb-0 flex items-center justify-between border-b border-black/10 shrink-0">
            <a
              href="https://www.the3dapp.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="The 3D App home"
              className="-ml-5 flex items-center"
            >
              <img
                src={dashboardLogo}
                alt="The 3D App"
                className="h-12 w-auto object-contain"
              />
            </a>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
              className="w-9 h-9 flex items-center justify-center bg-transparent border-0 focus:outline-none text-black/85 p-1"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-2">
            <MobileNavLink
              to={ROUTES.PERSONAL_DASHBOARD}
              active={isActive(ROUTES.PERSONAL_DASHBOARD)}
              label="Dashboard"
              onNavigate={() => setMobileMenuOpen(false)}
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                >
                  <rect x="2" y="2" width="7" height="7" rx="1.5" />
                  <rect x="11" y="2" width="7" height="7" rx="1.5" />
                  <rect x="2" y="11" width="7" height="7" rx="1.5" />
                  <rect x="11" y="11" width="7" height="7" rx="1.5" />
                </svg>
              }
            />
            <MobileNavLink
              to={ROUTES.PERSONAL_TOUR_UPLOAD}
              active={isActive(ROUTES.PERSONAL_TOUR_UPLOAD)}
              label="Upload tour"
              onNavigate={() => setMobileMenuOpen(false)}
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                >
                  <path d="M10 13V4" />
                  <path d="M6.5 7.5 10 4l3.5 3.5" />
                  <path d="M3.5 12.5v2.5a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5v-2.5" />
                </svg>
              }
            />
            <button
              type="button"
              onClick={() =>
                window.open(
                  'https://www.the3dapp.com/book',
                  '_blank',
                  'noreferrer',
                )
              }
              className="flex items-center gap-4 px-5 py-4 text-[17px] font-medium text-black/75 w-full text-left bg-transparent border-0 focus:outline-none cursor-pointer"
            >
              <span className="flex items-center justify-center w-6 h-6 text-black/70">
                <CalendarIcon size={20} />
              </span>
              <span>Book a Tour</span>
            </button>
            <button
              type="button"
              onClick={() =>
                window.open(
                  'https://www.the3dapp.com/applications/diy',
                  '_blank',
                  'noreferrer',
                )
              }
              className="flex items-center gap-4 px-5 py-4 text-[17px] font-medium text-black/75 w-full text-left bg-transparent border-0 focus:outline-none cursor-pointer"
            >
              <span className="flex items-center justify-center w-6 h-6 text-black/70">
                <ToolsIcon size={20} />
              </span>
              <span>DIY</span>
            </button>
            <button
              type="button"
              className="flex items-center gap-4 px-5 py-4 text-[17px] font-medium text-black/75 w-full text-left bg-transparent border-0 focus:outline-none cursor-pointer"
            >
              <span className="flex items-center justify-center w-6 h-6 text-black/70">
                <PlayIcon size={20} />
              </span>
              <span>Tutorials</span>
            </button>
          </nav>

          <div className="border-t border-black/10 px-5 py-4 shrink-0">
            <Link
              to={ROUTES.PERSONAL_PROFILE_EDIT}
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Profile settings"
              className="flex items-center gap-3 mb-3 -mx-1 px-1 py-1 rounded-md hover:bg-black/[0.04] transition-colors"
            >
              <span className="w-10 h-10 rounded-full bg-black/85 text-white flex items-center justify-center font-semibold text-[13px] shadow-sm">
                {getInitials(displayName)}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-[14px] text-black/[0.9] truncate">
                  {displayName}
                </span>
                {user?.email && user.email !== displayName && (
                  <span className="block text-[12px] text-black/50 truncate">
                    {user.email}
                  </span>
                )}
              </span>
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full px-1 py-2 text-[15px] font-medium text-black/75 hover:text-black/[0.9] bg-transparent border-0 focus:outline-none cursor-pointer"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 12l3-4-3-4M13 8H6M6 3H3v10h3" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardLayout;
